import {Plural, Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {styled} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {emojiMap} from '../../../services/emojis/emojis';
import {OkCancelDialog} from '../../common/dialogs/okCancelDialog';
import {InlineEmoji} from '../../common/emoji/inlineEmoji';
import {CutoffTimeProps} from './massArchiveCutoffDropdown';
import {MassArchiveCutoffDropdownBox} from './massArchiveCutoffDropdownBox';

const emailCountCutoff = 10000;

/*
 * Props.
 */

export interface MassArchiveDialogProps {
  emailCount: number | undefined;
  selectedCutoff: CutoffTimeProps;
  onCutoffSelect: HandlerOf<CutoffTimeProps>;
  onLearnMoreClick: Handler;
  onMassArchiveClick: Handler;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledDropdownBoxDiv = styled.div`
  margin-top: -6px;
`;

/*
 * Component.
 */

export const MassArchiveDialog: FC<MassArchiveDialogProps> = (props) => {
  const {emailCount} = props;
  const canMassArchive = isDefined(emailCount) && emailCount > 0;

  const renderOkButtonLabel = () => {
    if (isUndefined(emailCount)) {
      return <Trans id="dialog-massarchive-okbutton-unknown">Archive emails</Trans>;
    }

    if (emailCount > emailCountCutoff) {
      return <Trans id="dialog-massarchive-okbutton-10k">Archive 10k+ emails</Trans>;
    }

    return (
      <Plural
        id="dialog-massarchive-okbutton"
        value={emailCount}
        one="Archive # email"
        other="Archive # emails"
      />
    );
  };

  const title = (
    <Trans id="dialog-massarchive-title">
      Start with a clean slate <InlineEmoji emoji={emojiMap.sparkles} size={18} />
    </Trans>
  );

  const {selectedCutoff, onCutoffSelect, onLearnMoreClick, onMassArchiveClick, onRequestClose} = props;
  return (
    <OkCancelDialog
      title={title}
      okButtonLabel={renderOkButtonLabel()}
      isOkDisabled={!canMassArchive}
      cancelButtonLabel={<Trans id="dialog-massarchive-button-notnow">Not now</Trans>}
      onOkClick={onMassArchiveClick}
      onRequestClose={onRequestClose}
      disableSameWidth
    >
      <Trans id="dialog-massarchive-description">
        Import complete! Clean up your inbox and archive the emails that don’t need a reply. You can easily
        access them in the Archived tab.{' '}
        {
          <InlineLinkButton onClick={onLearnMoreClick}>
            <Trans id="dialog-massarchive-learnmore">Learn more</Trans>
          </InlineLinkButton>
        }
        <br />
        <br />
        Archive all emails received before:
      </Trans>
      <StyledDropdownBoxDiv>
        <MassArchiveCutoffDropdownBox selectedCutoff={selectedCutoff} onCutoffSelect={onCutoffSelect} />
      </StyledDropdownBoxDiv>
    </OkCancelDialog>
  );
};
